import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const ROLE_SUGGESTIONS = [
  "Project Manager", "Superintendent", "Foreman", "Crew Lead",
  "Office Manager", "Estimator", "Salesperson", "Other",
];

const emptyMember = () => ({
  name: "", role: "", email: "", phone: "", preferred_language: "",
});

// PO 2026-02 · Contractor Onboarding · Key Team Members (optional).
// Writes each row to POST /api/assignees with kind='individual' via the
// existing canonical db.assignees owner. NO writes on Skip. NO
// scheduling / work-order / send side effect. Free-text role; UI-only
// preset chips (not a backend enum, not RBAC).
export default function KeyTeamMembersStep({ onDone }) {
  const [rows, setRows] = useState([emptyMember()]);
  const [saving, setSaving] = useState(false);

  const patch = (idx, field, value) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const addAnother = () => setRows((r) => [...r, emptyMember()]);
  const skip = () => onDone?.({ saved: 0 });

  const submit = async () => {
    const token = localStorage.getItem("jobclose_token") || localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const clean = rows.filter((r) => (r.name || "").trim().length > 0);
    if (clean.length === 0) return skip();
    setSaving(true);
    let saved = 0;
    for (const row of clean) {
      try {
        await axios.post(`${API}/api/assignees`, {
          kind: "individual",
          display_name: row.name.trim(),
          role: row.role.trim(),
          email: row.email.trim(),
          phone: row.phone.trim(),
          preferred_language: row.preferred_language,
          active: true,
        }, { headers });
        saved += 1;
      } catch (e) {
        toast.error(`Could not save ${row.name}`);
      }
    }
    setSaving(false);
    toast.success(saved === 1 ? "1 team member saved" : `${saved} team members saved`);
    onDone?.({ saved });
  };

  return (
    <div data-testid="onboarding-team-step" className="space-y-6 p-6">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-semibold">Key team members</h2>
          <Badge variant="secondary">Optional</Badge>
        </div>
        <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
          Add the people who help manage or run your jobs. You can skip this and add them later.
        </p>
      </div>
      {rows.map((row, idx) => (
        <div key={idx} className="grid gap-3 rounded-lg border p-4">
          <Input
            data-testid={`team-member-name-${idx}`}
            placeholder="Name (e.g. Manuel)"
            value={row.name}
            onChange={(e) => patch(idx, "name", e.target.value)}
          />
          <Input
            data-testid={`team-member-role-${idx}`}
            placeholder="Role / Title (e.g. Project Manager)"
            value={row.role}
            onChange={(e) => patch(idx, "role", e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            {ROLE_SUGGESTIONS.map((r) => (
              <button
                key={r}
                type="button"
                className="rounded-full border px-3 py-1 text-xs hover:bg-slate-50"
                onClick={() => patch(idx, "role", r)}
              >
                {r}
              </button>
            ))}
          </div>
          <Input
            data-testid={`team-member-email-${idx}`}
            type="email"
            placeholder="Email"
            value={row.email}
            onChange={(e) => patch(idx, "email", e.target.value)}
          />
          <Input
            data-testid={`team-member-phone-${idx}`}
            placeholder="Phone"
            value={row.phone}
            onChange={(e) => patch(idx, "phone", e.target.value)}
          />
          <select
            data-testid={`team-member-language-${idx}`}
            className="h-9 rounded-md border bg-white px-3 text-sm"
            value={row.preferred_language}
            onChange={(e) => patch(idx, "preferred_language", e.target.value)}
          >
            <option value="">Preferred language (default)</option>
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </select>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="team-member-add-another"
          variant="outline"
          type="button"
          onClick={addAnother}
        >
          + Add another team member
        </Button>
        <div className="flex-1" />
        <Button
          data-testid="team-member-skip"
          variant="ghost"
          type="button"
          onClick={skip}
          disabled={saving}
        >
          Skip for now
        </Button>
        <Button
          data-testid="team-member-continue"
          type="button"
          onClick={submit}
          disabled={saving}
        >
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </div>
  );
}
